/* eslint-disable eag/match-named-export, react/only-export-components -- Pre-existing grab-bag file with several exports. Pre-existing violation */
import {captureException} from '@sentry/browser';
import {
  Flag,
  IconButton,
  showNotification,
  Tooltip,
  type Column,
  type FlagProps,
  type ValidationErrors,
} from 'platform/components';
import {Box, HStack, Icon, Show, Text, VStack, type ThemeIconKey} from 'platform/foundation';
import styled from 'styled-components';
import {z} from 'zod';

import {useState, type ReactNode} from 'react';

import {isNotNil} from 'ramda';

import {
  isNewVehicleConflict,
  type ModelYearReadinessCheckResponseBody,
  type ModelYearReadinessSeverity,
} from '@dms/api/new-vehicle';
import {VehicleTypeEnumObject} from '@dms/api/shared';
import {getApiErrorMessages, getApiErrorOrigin, type ApiErrorOrigin} from '@dms/api/utils';
import {getNewVehicleEnumLabel, MoneyText} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {isFetchBaseQueryError, isNotNilOrEmpty, type ApiError, type Nullish} from 'shared/web';

import {HEX_COLOUR} from '../../../components/ColourSwatch/utils/getSwatchColor';
import {isApiError} from './isApiError';

export const t = (key: string, defaultValue?: string, options?: Record<string, unknown>) =>
  defaultValue === undefined ? i18n.t(key, options) : i18n.t(key, {defaultValue, ...options});

export const failingChecks = (
  checks: ModelYearReadinessCheckResponseBody[],
  severity: ModelYearReadinessSeverity
) => checks.filter((check) => !check.passed && check.severity === severity);

export const PASSENGER_CAR = VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR;
export const HYBRID_FUEL = 'FUELTYPE_HYBRID';
/** The hybrid type only means something for a hybrid fuel; anything else always sends null. */
export const engineHybridType = (values: {fuel: string; hybridType?: string | Nullish}) =>
  values.fuel === HYBRID_FUEL ? values.hybridType || null : null;

export const optionalText = z
  .string()
  .nullable()
  .transform((value) => value ?? '');

// Backend contract: `format: date` (real calendar day), `^#[0-9A-Fa-f]{6}$`, power `^\d{1,5}(\.\d{1,2})?$`.
const calendarDate = z.string().date();
// Equivalent to /^\d{1,5}(\.\d{1,2})?$/ without the optional group the linter flags; 0 kW is no engine.
const isPower = (value: string) =>
  (/^\d{1,5}$/.test(value) || /^\d{1,5}\.\d{1,2}$/.test(value)) && Number(value) > 0;
const toDecimalDot = (value: string) => value.replace(',', '.');
const NON_NEGATIVE_INT = /^\d+$/;
/** Same words as the date field's own message, which already shows the expected format. */
export const invalidDateMessage = () => t('general.notifications.invalidDate', 'Invalid date');

const invalidDate = () => ({message: invalidDateMessage()});
const invalidPower = () => ({
  message: t(
    'entity.newVehicle.catalogue.validation.invalidPower',
    'Enter a power above 0 with up to 2 decimal places.'
  ),
});
const invalidWholeNumber = () => ({
  message: t('entity.newVehicle.catalogue.validation.invalidWholeNumber', 'Enter a whole number.'),
});
const invalidHex = () => ({
  message: t('entity.newVehicle.catalogue.validation.invalidHex', 'Enter a colour like #1A2B3C.'),
});
export const isCalendarDate = (value: string) => calendarDate.safeParse(value).success;
export const isoDate = z.string().refine(isCalendarDate, invalidDate);
/** '' or null stay as given so callers keep their existing nullable/empty shapes. */
export const optionalIsoDate = z
  .string()
  .nullable()
  .refine((value) => !value || isCalendarDate(value), invalidDate);
const MAKE_DATA_KEY = /^MAKE_[A-Z0-9_]+$/;
const MODEL_DATA_KEY = /^MAKE_[A-Z0-9_]+-MODELFAMILY_[A-Z0-9_]+$/;
/** Placeholder keys stand for a custom entry whose real name is stored beside them. */
const PLACEHOLDER_MAKE_KEY = 'MAKE_OTHER';
const PLACEHOLDER_MODEL_SUFFIX = '-MODELFAMILY_OTHER';

/**
 * Links a catalogue make to the shared vehicle catalogue. Empty means the entry is custom, which
 * materialises an ordered vehicle as the MAKE_OTHER placeholder carrying the authored name.
 */
export const makeVehicleDataKey = (persisted: string | Nullish) =>
  optionalText.refine(
    (value) =>
      value === '' ||
      value === (persisted ?? '') ||
      (MAKE_DATA_KEY.test(value) && value !== PLACEHOLDER_MAKE_KEY),
    () => ({
      message: t(
        'entity.newVehicle.catalogue.validation.invalidMakeDataKey',
        'Enter a vehicle catalogue key such as MAKE_SKODA, or leave empty for a make of your own.'
      ),
    })
  );

/**
 * Links a catalogue model to the shared vehicle catalogue. The key carries its make, so it is only
 * meaningful next to that make's key: a model key under a make without one names an official model
 * of a make nobody recognises, and the ordered vehicle then shows the raw key instead of a name.
 */
export const modelVehicleDataKey = (makeKey: string | Nullish, persisted: string | Nullish) =>
  optionalText.superRefine((value, ctx) => {
    // A key already stored stays acceptable: the update is a PATCH that ignores nulls, so an
    // existing bad key cannot be cleared here, and refusing it would block editing the rest of
    // the record for no gain.
    if (value === '' || value === (persisted ?? '')) {
      return;
    }
    if (!MODEL_DATA_KEY.test(value) || value.endsWith(PLACEHOLDER_MODEL_SUFFIX)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t(
          'entity.newVehicle.catalogue.validation.invalidModelDataKey',
          'Enter a vehicle catalogue key such as MAKE_SKODA-MODELFAMILY_OCTAVIA, or leave empty for a model of your own.'
        ),
      });
      return;
    }
    if (!makeKey || !value.startsWith(`${makeKey}-MODELFAMILY_`)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t(
          'entity.newVehicle.catalogue.validation.modelDataKeyMakeMismatch',
          'This key belongs to a different make. Give the make the matching key first, or leave this empty.'
        ),
      });
    }
  });

export const optionalHexColour = optionalText.refine(
  (value) => value === '' || HEX_COLOUR.test(value),
  invalidHex
);
export const powerInput = z.string().transform(toDecimalDot).refine(isPower, invalidPower);
export const optionalPowerInput = optionalText
  .transform(toDecimalDot)
  .refine((value) => value === '' || isPower(value), invalidPower);
export const optionalNonNegativeIntInput = optionalText.refine(
  (value) => value === '' || NON_NEGATIVE_INT.test(value),
  invalidWholeNumber
);
const isNonNegativeDecimal = (value: string) => /^\d+$/.test(value) || /^\d+\.\d+$/.test(value);
export const optionalDecimalInput = optionalText.transform(toDecimalDot).refine(
  (value) => value === '' || isNonNegativeDecimal(value),
  () => ({
    message: t(
      'general.errors.number.greaterOrEqual',
      'Number must be greater than or equal to {min}',
      {
        min: 0,
      }
    ),
  })
);
/** superRefine step: issue on `path` when `to` is earlier than `from`. Equal dates are valid. */
export const dateWindowRefinement =
  <T extends Record<string, unknown>>(
    fromKey: keyof T & string,
    toKey: keyof T & string,
    path = toKey
  ) =>
  (values: T, ctx: z.RefinementCtx) => {
    const from = values[fromKey];
    const to = values[toKey];
    if (
      typeof from === 'string' &&
      typeof to === 'string' &&
      isCalendarDate(from) &&
      isCalendarDate(to) &&
      to < from
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [path],
        message: t(
          'entity.newVehicle.catalogue.validation.endBeforeStart',
          "End date can't be earlier than the start date."
        ),
      });
    }
  };

export const joinParts = (parts: (string | Nullish | false)[]) =>
  parts
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .join(' · ');

const backendErrorBody = z.object({
  errors: z.array(z.object({code: z.string().optional(), message: z.string()})).optional(),
  validationErrors: z.array(z.object({field: z.string(), message: z.string()})).optional(),
});
const parseErrorBody = (error: unknown) =>
  typeof error === 'object' && error !== null && 'data' in error
    ? backendErrorBody.safeParse(error.data).data
    : undefined;
export const catalogueErrorCodes = (error: unknown) =>
  (parseErrorBody(error)?.errors ?? []).flatMap(({code}) => (code ? [code] : []));
/** Schema violations: `field` names the request property, as `pricelistVersion.label`. */
export const catalogueValidationErrors = (error: unknown) =>
  parseErrorBody(error)?.validationErrors ?? [];

const DUPLICATE_NAME = 'DMS-NV-CT-0016';
const STALE_CATALOGUE_VERSION = 'DMS-NV-CT-0012';
/** Only this 409 means another write won; the other catalogue conflicts carry their own reason. */
export const isStaleCatalogueVersion = (error: unknown) =>
  isNewVehicleConflict(error) && catalogueErrorCodes(error).includes(STALE_CATALOGUE_VERSION);
export const VEHICLE_CATALOGUE_UNAVAILABLE = 'DMS-NV-CT-0042';
/**
 * Vehicle catalogue key rejections from make and model writes. The backend only ships English for
 * them and quotes raw keys, so they are worded here without the keys. All but the outage belong
 * to the key field.
 */
const VEHICLE_DATA_KEY_ERRORS: Record<string, () => string> = {
  'DMS-NV-CT-0039': () =>
    t(
      'entity.newVehicle.errors.unknownMakeKey',
      "This make isn't in the vehicle catalogue. Choose another make or leave it unlinked."
    ),
  'DMS-NV-CT-0040': () =>
    t(
      'entity.newVehicle.errors.modelKeyOfOtherMake',
      "This model isn't in the vehicle catalogue under the make's catalogue entry. Choose another model or leave it unlinked."
    ),
  'DMS-NV-CT-0041': () =>
    t(
      'entity.newVehicle.errors.makeKeyInUse',
      "The make's catalogue entry can't change while its models are linked to the vehicle catalogue. Unlink those models first."
    ),
  [VEHICLE_CATALOGUE_UNAVAILABLE]: () =>
    t(
      'entity.newVehicle.errors.vehicleCatalogueUnavailable',
      "The vehicle catalogue isn't available right now, so the link can't be checked. Your changes are kept; try again in a moment."
    ),
  'DMS-NV-CT-0043': () =>
    t(
      'entity.newVehicle.errors.duplicateVehicleDataKey',
      'Another make or model is already linked to this vehicle catalogue entry. Use that one instead.'
    ),
};

/**
 * Field rejections from engine, trim, colour, upholstery and model writes: vehicle catalogue
 * codelists and the online trim's missing type. The backend message is translated, so it is shown
 * on the field as is.
 */
const FIELD_ERRORS: Record<string, string> = {
  'DMS-NV-CT-0025': 'type',
  'DMS-NV-CT-0045': 'fuel',
  'DMS-NV-CT-0046': 'hybridType',
  'DMS-NV-CT-0047': 'transmission',
  'DMS-NV-CT-0048': 'drive',
  'DMS-NV-CT-0049': 'bodyStyle',
  'DMS-NV-CT-0050': 'kind',
  'DMS-NV-CT-0051': 'fabric',
  'DMS-NV-CT-0052': 'hybridType',
  'DMS-NV-CT-0067': 'effectiveFrom',
};
/**
 * A code another variant, engine or colour of the model year already uses. The backend message is
 * translated and names that record, so it is shown on the code field as is.
 */
const CODE_CONFLICT_FIELDS: Record<string, 'orderCode' | 'factoryCode'> = {
  'DMS-NV-CT-0053': 'orderCode',
  'DMS-NV-CT-0055': 'factoryCode',
  'DMS-NV-CT-0056': 'factoryCode',
};
/** The backend message of a code-in-use rejection. */
export const catalogueCodeConflict = (error: unknown) =>
  parseErrorBody(error)?.errors?.find(({code}) => code && CODE_CONFLICT_FIELDS[code])?.message;

/** Schema `field: message` lines, then coded errors: key rejections translated, the rest verbatim. */
export const catalogueErrorMessages = (error: ApiError): string[] => {
  const body = isFetchBaseQueryError(error) ? parseErrorBody(error) : undefined;
  if (!body) {
    return [];
  }
  return [
    ...(body.validationErrors ?? []).map(({field, message}) => `${field}: ${message}`),
    ...(body.errors ?? [])
      .filter(({code}) => code?.startsWith('DMS-NV-'))
      .map(({code, message}) => (code && VEHICLE_DATA_KEY_ERRORS[code]?.()) || message),
  ];
};
export const catalogueErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }
  const apiError = error as ApiError;
  const messages = catalogueErrorMessages(apiError);
  if (messages.length) {
    return messages.join('\n');
  }
  const fallback = getApiErrorMessages(apiError);
  return Array.isArray(fallback) ? fallback.join('\n') : fallback;
};
export const warnStaleCatalogueVersion = () =>
  showNotification.warning(
    t(
      'entity.newVehicle.catalogue.reloadAggregate',
      'This catalogue record changed. Reload the aggregate before saving again.'
    )
  );

/**
 * An error whose message is written for the user, as a batch too large to send.
 * `handleCatalogueError` shows its message; any other thrown `Error` is a bug.
 */
export class UserFacingCatalogueError extends Error {
  /**
   * Set by a subclass for a refusal the user can act on (`business`), so its toast offers no AI
   * bug report. Unset keeps the report.
   */
  errorOrigin?: ApiErrorOrigin;
}

/**
 * Pass `setErrors` from a catalogue form to show name, key, codelist, trim type, effective date and
 * code-in-use rejections on their fields; everything else, and every rejection without a form,
 * becomes a notification. A thrown error that is not a rejected request is a bug: it is captured
 * and the save fails with a generic message, so a form submit still settles.
 */
export const handleCatalogueError = (
  error: unknown,
  setErrors?: (errors: ValidationErrors | null) => void
) => {
  if (error instanceof UserFacingCatalogueError) {
    if (isNotNil(error.errorOrigin)) {
      showNotification.error(error.message, {errorOrigin: error.errorOrigin});
    } else {
      showNotification.error(error.message);
    }
    return;
  }
  if (!isApiError(error)) {
    captureException(error);
    showNotification.error(t('general.labels.somethingWentWrong', 'Something went wrong.'));
    return;
  }
  const messages = catalogueErrorMessages(error);
  const codes = catalogueErrorCodes(error);
  if (setErrors) {
    const codedFieldErrors = (parseErrorBody(error)?.errors ?? []).flatMap(({code, message}) => {
      const name = code && (FIELD_ERRORS[code] ?? CODE_CONFLICT_FIELDS[code]);
      return name ? [{name, message}] : [];
    });
    const fieldErrors = codes.flatMap((code) => {
      if (code === DUPLICATE_NAME) {
        return [
          {
            name: 'name',
            message: t(
              'entity.newVehicle.errors.duplicateMakeOrModelName',
              'This name is already used. Names are compared without letter case, so pick the existing entry or a different name.'
            ),
          },
        ];
      }
      const keyMessage = code !== VEHICLE_CATALOGUE_UNAVAILABLE && VEHICLE_DATA_KEY_ERRORS[code];
      return keyMessage ? [{name: 'vehicleDataKey', message: keyMessage()}] : [];
    });
    if (fieldErrors.length || codedFieldErrors.length) {
      setErrors([...fieldErrors, ...codedFieldErrors]);
      return;
    }
  }
  if (isStaleCatalogueVersion(error)) {
    warnStaleCatalogueVersion();
    return;
  }
  if (messages.length) {
    showNotification.error(messages, {errorOrigin: getApiErrorOrigin(error).origin});
    return;
  }
  handleApiError(error);
};

type CellAlign = 'left' | 'center' | 'right';

const JUSTIFY = {left: 'flex-start', center: 'center', right: 'flex-end'} as const;

export function Cell({
  children,
  align,
}: {
  children: React.ReactNode;
  /** Centre a tick under its header, right-align money and counts; text cells stay left. */
  align?: CellAlign;
  role?: 'cell' | 'gridcell';
  ariaColIndex?: number;
  ariaSelected?: boolean;
}) {
  return (
    <Box paddingHorizontal={2} paddingVertical={2}>
      {align && align !== 'left' ? (
        <HStack align="center" justify={JUSTIFY[align]}>
          <Text size="small">{children}</Text>
        </HStack>
      ) : (
        <Text size="small">{children}</Text>
      )}
    </Box>
  );
}

/**
 * A bare `th` centres its content and carries no padding, so raw column labels never line up with
 * the cells below them. Every catalogue table header goes through here to share Cell's padding and
 * alignment.
 */
export const column = (
  label: ReactNode,
  options?: {width?: Column['width']; align?: CellAlign}
): Column => ({
  width: options?.width,
  element: (
    <Box paddingHorizontal={2} paddingVertical={2}>
      <HStack align="center" justify={JUSTIFY[options?.align ?? 'left']}>
        {typeof label === 'string' ? (
          <Text size="xSmall" color="secondary" alternative align={options?.align ?? 'left'}>
            {label}
          </Text>
        ) : (
          label
        )}
      </HStack>
    </Box>
  ),
});

/** A titled group of form fields, so long dialogs read as a few decisions instead of one list. */
export function FormSection(props: {title: string; children: ReactNode}) {
  return (
    <VStack spacing={2}>
      <Text size="xSmall" color="secondary" alternative>
        {props.title}
      </Text>
      {props.children}
    </VStack>
  );
}

/** One line under a tab that says what the tab is for and how its records reach the configurator. */
export function TabIntro(props: {children: string}) {
  return (
    <Box paddingBottom={1} maxWidth={200}>
      <Text size="small" color="secondary">
        {props.children}
      </Text>
    </Box>
  );
}

/** Trailing column for row actions: hugs the right edge so the data columns keep the width. */
export const actionsColumn = (): Column => ({width: '1%', element: ''});

type RowAction = {icon: ThemeIconKey; label: string; onClick: () => void};

export const editRowAction = (onClick: () => void): RowAction => ({
  icon: 'editor/mode',
  label: t('general.actions.edit', 'Edit'),
  onClick,
});
export const archiveRowAction = (onClick: () => void): RowAction => ({
  icon: 'content/archive',
  label: t('general.actions.archive', 'Archive'),
  onClick,
});

/** Icon actions for a row whose click opens the editor; their clicks must not reach the row. */
export function RowActions(props: {actions: RowAction[]}) {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- only stops propagation; the buttons inside carry the interaction
    <div onClick={(event) => event.stopPropagation()}>
      <HStack spacing={1} justify="flex-end">
        {props.actions.map((action) => (
          <Tooltip key={action.label} label={action.label}>
            <IconButton icon={action.icon} aria-label={action.label} onClick={action.onClick} />
          </Tooltip>
        ))}
      </HStack>
    </div>
  );
}

export const NotOfferedFlag = () => (
  <Flag
    label={t('entity.newVehicle.catalogue.notOfferedOnAnyTrim', 'Not offered on any trim')}
    colorScheme="orange"
    isSubtle
  />
);

/**
 * Where a colour or upholstery can be ordered. An item on no trim is invisible in the configurator,
 * which is the usual reason its publish check fails, so that state is flagged rather than shown as 0.
 */
export function OfferedOn(props: {trimIds: string[]; trims: {id: string; name: string}[]}) {
  const names = props.trims.filter(({id}) => props.trimIds.includes(id)).map(({name}) => name);
  if (!names.length) {
    return <NotOfferedFlag />;
  }
  return names.length === props.trims.length
    ? t('entity.newVehicle.catalogue.allTrims', 'All trims')
    : names.join(', ');
}

export const inclusionFlag = (inclusion: 'STANDARD' | 'OPTIONAL' | null): FlagProps =>
  inclusion === 'STANDARD' // eslint-disable-line no-nested-ternary -- Pre-existing violation
    ? {
        label: getNewVehicleEnumLabel('inclusion', inclusion, t),
        colorScheme: 'green',
        isSubtle: true,
      }
    : inclusion === 'OPTIONAL'
      ? {
          label: getNewVehicleEnumLabel('inclusion', inclusion, t),
          colorScheme: 'blue',
          isSubtle: true,
        }
      : {
          label: t('entity.newVehicle.catalogue.notOffered', 'Not offered'),
          colorScheme: 'neutral',
          isSubtle: true,
        };

const ColourSwatchCircle = styled.span<{$hex?: string}>`
  display: inline-block;
  flex-shrink: 0;
  width: ${({theme}) => theme.getSize(6)};
  height: ${({theme}) => theme.getSize(6)};
  border-radius: ${({theme}) => theme.radii.circular};
  ${({theme, $hex}) =>
    $hex ? `border: 1px solid ${theme.colors.general.separator}; background-color: ${$hex};` : ''}
`;

const ColourSwatchTile = styled.span<{$hex?: string}>`
  display: inline-block;
  flex-shrink: 0;
  width: ${({theme}) => theme.getSize(8)};
  height: ${({theme}) => theme.getSize(8)};
  border-radius: ${({theme}) => theme.radii.small};
  background-color: ${({$hex}) => $hex ?? 'transparent'};
  /* A real colour gets a hairline for depth; a missing one reads as an empty, dashed slot. */
  ${({theme, $hex}) =>
    $hex
      ? `box-shadow: inset 0 0 0 1px ${theme.colors.palettes.neutral[900][20]};`
      : `border: 1px dashed ${theme.colors.palettes.neutral[40][100]};`}
`;

const validHex = (hex: string | Nullish) => (hex && HEX_COLOUR.test(hex) ? hex : undefined);

/**
 * Renders nothing for a missing or malformed hex, so callers can pass raw record values.
 * `reserveSlot` keeps an empty circle's space instead, so names in a list stay aligned.
 */
export function ColourSwatch(props: {hex: string | Nullish; reserveSlot?: boolean}) {
  const hex = validHex(props.hex);
  return (
    <Show when={isNotNilOrEmpty(hex) || props.reserveSlot}>
      <ColourSwatchCircle aria-hidden $hex={hex} />
    </Show>
  );
}

/**
 * Whether a matrix axis shows swatches at all. Colours are optional in the catalogue, so an axis
 * with no colour data stays text-only instead of showing a row of empty boxes.
 */
export const hasAnySwatch = (items: {hex?: string | Nullish}[]) =>
  items.some(({hex}) => validHex(hex));

/** Surcharge label for a colour or upholstery: "incl." when free, otherwise "+price". */
function PriceHint(props: {amount: string; currency: string}) {
  // Display-only comparison; money stays a decimal string everywhere else.
  if (Number(props.amount) === 0) {
    return (
      <Text size="xSmall" color="secondary">
        {t('entity.newVehicle.catalogue.included', 'incl.')}
      </Text>
    );
  }
  return (
    <HStack spacing={0} align="center">
      <Text size="xSmall" color="secondary">
        +
      </Text>
      <MoneyText amount={props.amount} currency={props.currency} size="xSmall" color="secondary" />
    </HStack>
  );
}

/**
 * A matrix label with an optional swatch, name and surcharge, laid out as a row label or a column
 * header. `showSwatch` is decided per axis (see hasAnySwatch) so labels on one axis stay aligned.
 */
export function SwatchLabel(props: {
  name: string;
  hex?: string | Nullish;
  showSwatch: boolean;
  /** A colour has a price per trim, so it shows none here. */
  amount?: string;
  currency: string;
  direction: 'row' | 'column';
}) {
  const isColumn = props.direction === 'column';
  // eslint-disable-next-line eag/prefer-show -- Pre-existing violation
  const swatch = props.showSwatch ? (
    <ColourSwatchTile aria-hidden $hex={validHex(props.hex)} />
  ) : null;
  const text = (
    <VStack spacing={0} align={isColumn ? 'center' : 'flex-start'}>
      <Text size="small" alternative align={isColumn ? 'center' : 'left'}>
        {props.name}
      </Text>
      <Show when={isNotNilOrEmpty(props.amount) ? props.amount : null}>
        {(amount) => <PriceHint amount={amount} currency={props.currency} />}
      </Show>
    </VStack>
  );
  return isColumn ? (
    <VStack spacing={2} align="center">
      {swatch}
      {text}
    </VStack>
  ) : (
    <HStack spacing={3} align="center">
      {swatch}
      {text}
    </HStack>
  );
}

/** Width of a toggle column; the tile (24) fills it minus the cell padding (2 × 2). */
export const TOGGLE_COLUMN_WIDTH = 28;
const TOGGLE_TILE_WIDTH = 24;

const ToggleTileButton = styled.button<{$isOn: boolean}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({theme}) => theme.getSize(TOGGLE_TILE_WIDTH)};
  height: ${({theme}) => theme.getSize(10)};
  padding: 0;
  border: 1px ${({$isOn}) => ($isOn ? 'solid' : 'dashed')}
    ${({theme, $isOn}) =>
      $isOn ? theme.colors.palettes.green[60][100] : theme.colors.palettes.neutral[40][100]};
  border-radius: ${({theme}) => theme.radii.small};
  background-color: ${({theme, $isOn}) =>
    $isOn ? theme.colors.palettes.green[10][100] : theme.colors.general.white};
  cursor: pointer;
  user-select: none;
  transition-property: border-color, background-color, scale, opacity;
  transition-duration: 150ms;
  transition-timing-function: ease-out;

  &:hover:not(:disabled) {
    border-color: ${({theme, $isOn}) =>
      $isOn ? theme.colors.palettes.green[80][100] : theme.colors.palettes.neutral[300][100]};
    background-color: ${({theme, $isOn}) =>
      $isOn ? theme.colors.palettes.green[20][100] : theme.colors.palettes.neutral[10][100]};
  }

  &:active:not(:disabled) {
    scale: 0.96;
  }

  &:focus-visible {
    outline: 2px solid ${({theme}) => theme.colors.general.accent};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
  }

  &[aria-busy='true'] {
    cursor: progress;
    opacity: 0.6;
  }
`;

/**
 * A large click target for a yes/no matrix cell: green tick when allowed, dashed cross when not.
 * The new state shows at once and the tile locks until `onToggle` settles, so `onToggle` should
 * resolve only once the refreshed data is in; a rejection rolls the tile back to the prop value.
 */
export function ToggleTile(props: {
  isOn: boolean;
  isDisabled?: boolean;
  'aria-label': string;
  onToggle: () => Promise<unknown>;
}) {
  const [pending, setPending] = useState<boolean>();
  const isOn = pending ?? props.isOn;
  const isBusy = pending !== undefined;

  const toggle = () => {
    if (isBusy) {
      return;
    }
    setPending(!isOn);
    const settle = () => setPending(undefined);
    props.onToggle().then(settle, settle);
  };

  return (
    <ToggleTileButton
      type="button"
      role="checkbox"
      aria-checked={isOn}
      aria-busy={isBusy}
      aria-label={props['aria-label']}
      // Not `disabled` while busy: that would drop keyboard focus after every toggle.
      disabled={props.isDisabled}
      $isOn={isOn}
      onClick={toggle}
    >
      <Icon
        value={isOn ? 'navigation/check' : 'navigation/close'}
        size={isOn ? 5 : 4}
        color={isOn ? 'palettes.green.60.100' : 'palettes.neutral.300.100'}
      />
    </ToggleTileButton>
  );
}
